// ===================================================================
// SHELTER — QR Code Scanner / Lookup Tool
// Search survivors, pets, vehicles, property by QR code
// ===================================================================

import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Stack,
  SimpleGrid, Loader, Center, Avatar,
} from '../../components/gab/GabComponents';
import { IconSearch, IconQrcode, IconUser, IconPaw, IconCar, IconPackage, IconCamera, IconCameraOff } from '@tabler/icons-react';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { formatPhone } from '../../lib/phoneFormat';
import {
  SURVIVOR_STATUS_COLORS,
  SPECIAL_NEEDS_OPTIONS,
} from '../../types/shelter';
import type { Survivor, ShelterPet, ShelterVehicle, ShelterProperty } from '../../types/shelter';

type MatchType = 'survivor' | 'pet' | 'vehicle' | 'property';
interface MatchResult {
  type: MatchType;
  entity: Survivor | ShelterPet | ShelterVehicle | ShelterProperty;
}

export default function Lookup() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    survivors, pets, vehicles, property, shelters,
    loadingSurvivors, loadingPets, loadingVehicles, loadingProperty,
    loadSurvivors, loadPets, loadVehicles, loadProperty, loadShelters,
  } = useShelterStore();

  const [query, setQuery] = useState('');
  const [result, setResult] = useState<MatchResult | null>(null);
  const [searched, setSearched] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const scannerRef = useRef<InstanceType<typeof import('html5-qrcode').Html5Qrcode> | null>(null);
  const scannerContainerId = 'qr-scanner-container';

  useEffect(() => {
    loadSurvivors(); loadPets(); loadVehicles(); loadProperty(); loadShelters();
  }, [loadSurvivors, loadPets, loadVehicles, loadProperty, loadShelters]);

  // Store already server-side filters by active incident; use directly
  const iSurvivors = incidentId ? survivors : [];
  const iPets = incidentId ? pets : [];
  const iVehicles = incidentId ? vehicles : [];
  const iProperty = incidentId ? property : [];

  const isLoading = loadingSurvivors || loadingPets || loadingVehicles || loadingProperty;

  const shelterName = (id: string) => shelters.find(s => String(s.id) === id)?.name ?? id;
  const survivorName = (id: string) => {
    const s = survivors.find(sv => String(sv.id) === id);
    return s ? `${s.firstName} ${s.lastName}` : id;
  };

  // Search — defined before camera hooks so they can reference it
  const doSearch = useCallback((q: string) => {
    if (!q) return;
    setSearched(true);
    const ql = q.toLowerCase();

    // Search by QR code (exact match)
    const svQr = iSurvivors.find(s => s.qrCode && s.qrCode.toLowerCase() === ql);
    if (svQr) { setResult({ type: 'survivor', entity: svQr }); return; }

    // Search survivors by name, phone, email, or tag code
    const svName = iSurvivors.find(s =>
      `${s.firstName} ${s.lastName}`.toLowerCase().includes(ql) ||
      (s.lastName && s.lastName.toLowerCase() === ql) ||
      (s.phone && s.phone.includes(ql)) ||
      (s.email && s.email.toLowerCase().includes(ql))
    );
    if (svName) { setResult({ type: 'survivor', entity: svName }); return; }

    // Search pets by QR, name, or owner
    const pet = iPets.find(p =>
      (p.qrCode && p.qrCode.toLowerCase() === ql) ||
      (p.name && p.name.toLowerCase().includes(ql))
    );
    if (pet) { setResult({ type: 'pet', entity: pet }); return; }

    // Search vehicles by QR, plate, or make/model
    const vh = iVehicles.find(v =>
      (v.qrCode && v.qrCode.toLowerCase() === ql) ||
      (v.licensePlate && v.licensePlate.toLowerCase().includes(ql)) ||
      (`${v.make} ${v.model}`.toLowerCase().includes(ql))
    );
    if (vh) { setResult({ type: 'vehicle', entity: vh }); return; }

    // Search property by QR or description
    const pr = iProperty.find(p =>
      (p.qrCode && p.qrCode.toLowerCase() === ql) ||
      (p.description && p.description.toLowerCase().includes(ql))
    );
    if (pr) { setResult({ type: 'property', entity: pr }); return; }

    setResult(null);
  }, [iSurvivors, iPets, iVehicles, iProperty]);

  const handleSearch = () => doSearch(query.trim());

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  // Camera QR scanning
  const stopCamera = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) await scannerRef.current.stop();
      } catch { /* ignore */ }
      try { scannerRef.current.clear(); } catch { /* ignore */ }
      scannerRef.current = null;
    }
    setCameraActive(false);
  }, []);

  const startCamera = useCallback(async () => {
    if (scannerRef.current) await stopCamera();
    setCameraError('');
    setCameraActive(true);
    // Wait for container div to render
    await new Promise(r => setTimeout(r, 150));
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode(scannerContainerId);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          setQuery(decodedText);
          doSearch(decodedText);
          stopCamera();
        },
        () => { /* ignore scan failures */ },
      );
    } catch (err) {
      setCameraActive(false);
      setCameraError(err instanceof Error ? err.message : 'Camera not available. Check permissions.');
    }
  }, [stopCamera, doSearch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { stopCamera(); };
  }, [stopCamera]);

  const parseSpecialNeeds = (json: string): string[] => {
    try { return JSON.parse(json); } catch { return []; }
  };

  const getSpecialNeedLabel = (val: string) =>
    SPECIAL_NEEDS_OPTIONS.find(o => o.value === val)?.label ?? val;

  const renderSurvivor = (s: Survivor) => (
    <Paper p="lg" radius="md" withBorder>
      <Group gap="md" mb="md">
        <Avatar size={64} src={s.photoUrl || undefined} radius="xl" color="blue">
          <IconUser size={32} />
        </Avatar>
        <div>
          <Text size="lg" fw={700}>{s.firstName} {s.lastName}</Text>
          <Badge color={SURVIVOR_STATUS_COLORS[s.status]} size="sm">{(s.status || '').replace(/_/g, ' ')}</Badge>
        </div>
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <div><Text size="xs" fw={700} c="dimmed">QR Code</Text><Text size="sm">{s.qrCode}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Shelter</Text><Text size="sm">{s.shelterId ? shelterName(s.shelterId) : '-'}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Cot Assignment</Text><Text size="sm">{s.cotAssignment || '-'}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Phone</Text><Text size="sm">{s.phone ? formatPhone(s.phone) : '-'}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Intake</Text><Text size="sm">{s.intakeTimestamp ? new Date(s.intakeTimestamp).toLocaleString() : '-'}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Gender</Text><Text size="sm">{s.gender || '-'}</Text></div>
      </SimpleGrid>
      {parseSpecialNeeds(s.specialNeeds).length > 0 && (
        <Group gap={4} mt="sm">
          <Text size="xs" fw={700} c="dimmed">Special Needs:</Text>
          {parseSpecialNeeds(s.specialNeeds).map(n => (
            <Badge key={n} size="xs" variant="light" color="orange">{getSpecialNeedLabel(n)}</Badge>
          ))}
        </Group>
      )}
      <Button size="sm" mt="md" variant="light" onClick={() => navigate('/shelter/survivors')}>
        View Details
      </Button>
    </Paper>
  );

  const renderPet = (p: ShelterPet) => (
    <Paper p="lg" radius="md" withBorder>
      <Group gap="md" mb="md">
        <Avatar size={64} src={p.photoUrl || undefined} radius="xl" color="green">
          <IconPaw size={32} />
        </Avatar>
        <div>
          <Text size="lg" fw={700}>{p.name}</Text>
          <Badge size="sm">{p.status}</Badge>
        </div>
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <div><Text size="xs" fw={700} c="dimmed">Species</Text><Text size="sm">{p.species}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Breed</Text><Text size="sm">{p.breed || '-'}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Color</Text><Text size="sm">{p.color || '-'}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Owner</Text><Text size="sm">{p.ownerId ? survivorName(p.ownerId) : '-'}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">QR Code</Text><Text size="sm">{p.qrCode}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Location</Text><Text size="sm">{p.locationInShelter || '-'}</Text></div>
      </SimpleGrid>
      <Button size="sm" mt="md" variant="light" onClick={() => navigate('/shelter/pets')}>
        View Details
      </Button>
    </Paper>
  );

  const renderVehicle = (v: ShelterVehicle) => (
    <Paper p="lg" radius="md" withBorder>
      <Group gap="md" mb="md">
        <Avatar size={64} radius="xl" color="violet">
          <IconCar size={32} />
        </Avatar>
        <div>
          <Text size="lg" fw={700}>{v.year} {v.make} {v.model}</Text>
          <Badge size="sm">{v.status}</Badge>
        </div>
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <div><Text size="xs" fw={700} c="dimmed">License Plate</Text><Text size="sm">{v.licensePlate} ({v.state})</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Color</Text><Text size="sm">{v.color || '-'}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Owner</Text><Text size="sm">{v.ownerId ? survivorName(v.ownerId) : '-'}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Parking Location</Text><Text size="sm">{v.parkingLocation || '-'}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">QR Code</Text><Text size="sm">{v.qrCode}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">VIN</Text><Text size="sm">{v.vin || '-'}</Text></div>
      </SimpleGrid>
      <Button size="sm" mt="md" variant="light" onClick={() => navigate('/shelter/vehicles')}>
        View Details
      </Button>
    </Paper>
  );

  const renderProperty = (p: ShelterProperty) => (
    <Paper p="lg" radius="md" withBorder>
      <Group gap="md" mb="md">
        <Avatar size={64} radius="xl" color="teal">
          <IconPackage size={32} />
        </Avatar>
        <div>
          <Text size="lg" fw={700}>{p.description}</Text>
          <Badge size="sm">{p.category}</Badge>
        </div>
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <div><Text size="xs" fw={700} c="dimmed">Owner</Text><Text size="sm">{p.ownerId ? survivorName(p.ownerId) : '-'}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Storage Location</Text><Text size="sm">{p.storageLocation || '-'}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">QR Code</Text><Text size="sm">{p.qrCode}</Text></div>
        <div><Text size="xs" fw={700} c="dimmed">Checked In</Text><Text size="sm">{p.checkedInAt ? new Date(p.checkedInAt).toLocaleString() : '-'}</Text></div>
      </SimpleGrid>
      <Button size="sm" mt="md" variant="light" onClick={() => navigate('/shelter/property')}>
        View Details
      </Button>
    </Paper>
  );

  return (
    <Stack gap="lg">
      <Title order={3} c="black">QR Code Lookup</Title>

      <Paper p="lg" radius="md" withBorder>
        <Group align="flex-end" gap="sm">
          <TextInput
            label="Scan or enter QR code"
            placeholder="SHELTER-SV-001, SHELTER-PET-001, SHELTER-VH-001, SHELTER-PR-001"
            leftSection={<IconQrcode size={18} />}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ flex: 1 }}
            size="lg"
            autoFocus
          />
          <Button size="lg" leftSection={<IconSearch size={18} />} onClick={handleSearch} loading={isLoading}>
            Search
          </Button>
          <Button
            size="lg"
            variant={cameraActive ? 'filled' : 'light'}
            color={cameraActive ? 'red' : 'blue'}
            leftSection={cameraActive ? <IconCameraOff size={18} /> : <IconCamera size={18} />}
            onClick={cameraActive ? stopCamera : startCamera}
          >
            {cameraActive ? 'Stop' : 'Scan'}
          </Button>
        </Group>

        {cameraActive && (
          <div style={{ marginTop: 16 }}>
            <div id={scannerContainerId} style={{ width: '100%', maxWidth: 400, margin: '0 auto' }} />
            <Text size="xs" c="dimmed" ta="center" mt={8}>Point camera at QR code</Text>
          </div>
        )}

        {cameraError && (
          <Text size="sm" c="red" mt="sm">{cameraError}</Text>
        )}
      </Paper>

      {isLoading && (
        <Center py="xl"><Loader size="lg" /></Center>
      )}

      {searched && !isLoading && !result && (
        <Paper p="lg" radius="md" withBorder>
          <Center py="xl">
            <Stack align="center" gap="xs">
              <IconSearch size={48} color="gray" />
              <Text size="lg" fw={600} c="dimmed">No match found</Text>
              <Text size="sm" c="dimmed">No survivor, pet, vehicle, or property matches &quot;{query}&quot;</Text>
            </Stack>
          </Center>
        </Paper>
      )}

      {result && (
        <div>
          <Group gap="xs" mb="sm">
            <Badge size="lg" variant="light" color={
              result.type === 'survivor' ? 'blue' :
              result.type === 'pet' ? 'green' :
              result.type === 'vehicle' ? 'violet' : 'teal'
            }>
              {result.type.toUpperCase()}
            </Badge>
          </Group>
          {result.type === 'survivor' && renderSurvivor(result.entity as Survivor)}
          {result.type === 'pet' && renderPet(result.entity as ShelterPet)}
          {result.type === 'vehicle' && renderVehicle(result.entity as ShelterVehicle)}
          {result.type === 'property' && renderProperty(result.entity as ShelterProperty)}
        </div>
      )}
    </Stack>
  );
}
